import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit, MdMoreVert, MdSend } from 'react-icons/md';
import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { aiApi } from '@/services/api';
import { BanditIcon } from '../Common/BanditIcon';
import {
    useGlobalChatSessions,
    useCreateGlobalSession,
    useRenameChatSession,
    useDeleteChatSession,
    useChatHistory,
} from '@/services/hooks';
import { RenameDialog } from '../ImportedItems/RenameDialog';
import { ConfirmDialog } from '../Common/ConfirmDialog';

interface GlobalChatDrawerProps {
    isOpen: boolean;
    onClose: () => void;
}

export function GlobalChatDrawer({ isOpen, onClose }: GlobalChatDrawerProps) {
    const [messages, setMessages] = useState<{ role: 'user' | 'ai'; content: string }[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);

    const [sessionToRename, setSessionToRename] = useState<{ id: number; title: string } | null>(null);
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();
    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const [confirmConfig, setConfirmConfig] = useState<{
        title: string; message: string; action: () => Promise<void>;
        confirmText?: string; confirmColor?: string;
    } | null>(null);

    const toast = useToast();
    const chatScrollRef = useRef<HTMLDivElement>(null);

    // Theme colors
    const chatBg = useColorModeValue('gray.50', 'gray.900');
    const headerBg = useColorModeValue('white', 'gray.800');
    const inputBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const userBubbleBg = useColorModeValue('brand.500', 'brand.600');
    const aiBubbleBg = useColorModeValue('white', 'gray.800');
    const aiBubbleColor = useColorModeValue('gray.700', 'gray.200');
    const streamDotBg = useColorModeValue('gray.400', 'gray.500');

    // Queries
    const { data: sessions = [] } = useGlobalChatSessions();
    const { data: historyData } = useChatHistory({ session_id: currentSessionId || undefined });
    const createSessionMutation = useCreateGlobalSession();
    const renameSessionMutation = useRenameChatSession();
    const deleteSessionMutation = useDeleteChatSession();

    // Auto-scroll
    useEffect(() => {
        if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }, [messages, isStreaming]);

    // Pick first session
    useEffect(() => {
        if (sessions?.length && !currentSessionId) setCurrentSessionId(sessions[0].id);
    }, [sessions, currentSessionId]);

    // Load history
    useEffect(() => {
        if (historyData?.messages) {
            setMessages(historyData.messages.map((msg: any) => ({ role: msg.role, content: msg.content })));
        } else if (!currentSessionId) {
            setMessages([]);
        }
    }, [historyData, currentSessionId]);

    const handleSendMessage = async () => {
        if (!chatInput.trim() || isStreaming) return;
        const userMsg = chatInput;
        setChatInput('');
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setIsStreaming(true);

        try {
            let sessionId = currentSessionId;
            if (!sessionId) {
                const newSession = await createSessionMutation.mutateAsync({ title: userMsg.substring(0, 30) });
                sessionId = newSession.id;
                setCurrentSessionId(sessionId);
            }

            setMessages(prev => [...prev, { role: 'ai', content: '' }]);

            const fullTextRef = { current: '' };
            const displayedTextRef = { current: '' };

            const streamInterval = setInterval(() => {
                if (displayedTextRef.current.length < fullTextRef.current.length) {
                    const delta = fullTextRef.current.length - displayedTextRef.current.length;
                    const charsToAppend = delta > 100 ? Math.ceil(delta / 4) : Math.ceil(Math.random() * 3) + 2;
                    displayedTextRef.current = fullTextRef.current.substring(0, displayedTextRef.current.length + charsToAppend);

                    setMessages(prev => {
                        const updated = [...prev];
                        const last = updated[updated.length - 1];
                        if (last?.role === 'ai') {
                            last.content = displayedTextRef.current;
                        }
                        return [...updated];
                    });
                }
            }, 40);

            try {
                await aiApi.globalChatStream(userMsg, sessionId!, (chunk) => {
                    fullTextRef.current += chunk;
                });

                // Final catch-up
                let attempts = 0;
                while (displayedTextRef.current.length < fullTextRef.current.length && attempts < 50) {
                    await new Promise(r => setTimeout(r, 40));
                    attempts++;
                }
            } finally {
                clearInterval(streamInterval);
                setMessages(prev => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    if (last?.role === 'ai') last.content = fullTextRef.current;
                    return [...updated];
                });
            }
        } catch (error) {
            toast({ title: 'Error sending message', status: 'error', duration: 3000 });
            setMessages(prev => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === 'ai') last.content = 'Sorry, the AI connection was dropped. Please try again.';
                return updated;
            });
        } finally {
            setIsStreaming(false);
        }
    };

    const handleCreateSession = async () => {
        try {
            const s = await createSessionMutation.mutateAsync({ title: 'New Chat' });
            setCurrentSessionId(s.id);
            setMessages([]);
        } catch {
            toast({ title: 'Failed to create chat', status: 'error' });
        }
    };

    const handleRenameInit = (id: number, currentTitle: string) => {
        setSessionToRename({ id, title: currentTitle });
        onRenameOpen();
    };

    const handleRenameConfirm = async (newTitle: string) => {
        if (!sessionToRename) return;
        try {
            await renameSessionMutation.mutateAsync({ sessionId: sessionToRename.id, title: newTitle });
            onRenameClose();
            toast({ title: 'Chat renamed', status: 'success' });
        } catch {
            toast({ title: 'Failed to rename', status: 'error' });
        }
    };

    const handleDeleteInit = (id: number) => {
        setConfirmConfig({
            title: 'Delete Chat', message: 'Are you sure? This cannot be undone.',
            confirmText: 'Delete', confirmColor: 'red',
            action: async () => {
                await deleteSessionMutation.mutateAsync(id);
                if (currentSessionId === id) { setCurrentSessionId(null); setMessages([]); }
                onConfirmClose();
                toast({ title: 'Chat deleted', status: 'success' });
            },
        });
        onConfirmOpen();
    };

    return (
        <>
            <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton zIndex={10} />
                    <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} py={3}>
                        <HStack spacing={2}>
                            <BanditIcon boxSize={6} />
                            <Text>AI Agent</Text>
                        </HStack>
                    </DrawerHeader>

                    <DrawerBody p={0} display="flex" flexDirection="column">
                        {/* Session Tabs */}
                        <Flex p={2} px={3} borderBottomWidth="1px" borderColor={borderColor} bg={headerBg} align="center">
                            <HStack overflowX="auto" flex={1} spacing={1} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
                                {sessions.map((session: any) => {
                                    const isActive = currentSessionId === session.id;
                                    return (
                                        <Box key={session.id} display="inline-flex" alignItems="center">
                                            <Button
                                                size="xs"
                                                variant={isActive ? 'solid' : 'ghost'}
                                                colorScheme={isActive ? 'brand' : 'gray'}
                                                borderRightRadius={0}
                                                borderLeftRadius="full"
                                                onClick={() => setCurrentSessionId(session.id)}
                                                fontWeight={isActive ? 'bold' : 'normal'}
                                                pr={1}
                                            >
                                                {session.title}
                                            </Button>
                                            <Menu isLazy>
                                                <MenuButton
                                                    as={IconButton}
                                                    icon={<MdMoreVert />}
                                                    size="xs"
                                                    variant={isActive ? 'solid' : 'ghost'}
                                                    colorScheme={isActive ? 'brand' : 'gray'}
                                                    borderLeftRadius={0}
                                                    borderRightRadius="full"
                                                    aria-label="Options"
                                                    minW="auto"
                                                    px={1}
                                                />
                                                <MenuList zIndex={1400} minW="140px">
                                                    <MenuItem icon={<MdEdit />} fontSize="sm" onClick={() => handleRenameInit(session.id, session.title)}>Rename</MenuItem>
                                                    <MenuItem icon={<MdDelete />} fontSize="sm" onClick={() => handleDeleteInit(session.id)} color="red.500">Delete</MenuItem>
                                                </MenuList>
                                            </Menu>
                                        </Box>
                                    );
                                })}
                            </HStack>
                            <IconButton
                                aria-label="New Chat"
                                icon={<MdAdd />}
                                size="xs"
                                variant="ghost"
                                colorScheme="brand"
                                onClick={handleCreateSession}
                                borderRadius="full"
                                ml={1}
                            />
                        </Flex>

                        {/* Messages */}
                        <VStack flex={1} overflowY="auto" spacing={3} p={4} align="stretch" ref={chatScrollRef} bg={chatBg}>
                            {messages.length === 0 && (
                                <Flex direction="column" align="center" justify="center" flex={1} gap={3} py={12}>
                                    <BanditIcon boxSize={24} />
                                    <Text fontSize="lg" fontWeight="bold" color="brand.500">BidBandit AI Agent</Text>
                                    <Text fontSize="sm" color="gray.500" textAlign="center" maxW="300px">
                                        Ask me anything about your bids, pursuits, pipeline, or government contracting in general.
                                    </Text>
                                </Flex>
                            )}

                            {messages.map((msg, idx) => (
                                <Box
                                    key={idx}
                                    alignSelf={msg.role === 'user' ? 'end' : 'start'}
                                    bg={msg.role === 'user' ? userBubbleBg : aiBubbleBg}
                                    color={msg.role === 'user' ? 'white' : aiBubbleColor}
                                    maxW="85%"
                                    p={3}
                                    borderRadius="xl"
                                    borderBottomRightRadius={msg.role === 'user' ? 'sm' : 'xl'}
                                    borderBottomLeftRadius={msg.role === 'ai' ? 'sm' : 'xl'}
                                    boxShadow="sm"
                                    borderWidth={msg.role === 'ai' ? '1px' : '0'}
                                    borderColor={msg.role === 'ai' ? borderColor : 'transparent'}
                                >
                                    {msg.role === 'ai' ? (
                                        <Box className="markdown-body" fontSize="sm">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                                                p: ({ children }) => <Text fontSize="sm" mb={2} color="inherit">{children}</Text>,
                                                li: ({ children }) => <Box as="li" fontSize="sm" ml={4} mb={1} color="inherit">{children}</Box>,
                                                ul: ({ children }) => <Box as="ul" mb={2} color="inherit">{children}</Box>,
                                                ol: ({ children }) => <Box as="ol" mb={2} color="inherit">{children}</Box>,
                                            }}>{msg.content}</ReactMarkdown>
                                        </Box>
                                    ) : (
                                        <Text fontSize="sm" color="white" fontWeight="medium">{msg.content}</Text>
                                    )}
                                </Box>
                            ))}

                            {isStreaming && (
                                <Box alignSelf="start" bg={aiBubbleBg} borderWidth="1px" borderColor={borderColor} p={3} borderRadius="xl" borderBottomLeftRadius="sm" boxShadow="sm">
                                    <HStack spacing={1}>
                                        <Box w="6px" h="6px" bg={streamDotBg} borderRadius="full" animation="pulse 1.2s ease-in-out infinite" />
                                        <Box w="6px" h="6px" bg={streamDotBg} borderRadius="full" animation="pulse 1.2s ease-in-out infinite 0.2s" />
                                        <Box w="6px" h="6px" bg={streamDotBg} borderRadius="full" animation="pulse 1.2s ease-in-out infinite 0.4s" />
                                    </HStack>
                                </Box>
                            )}
                        </VStack>

                        {/* Input */}
                        <Box p={3} bg={headerBg} borderTopWidth="1px" borderColor={borderColor}>
                            <HStack spacing={2}>
                                <Input
                                    placeholder="Ask about your bids, pipeline..."
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                                    borderRadius="full"
                                    bg={inputBg}
                                    size="sm"
                                    border="none"
                                    _focus={{ boxShadow: '0 0 0 2px var(--chakra-colors-brand-500)' }}
                                />
                                <IconButton
                                    aria-label="Send"
                                    icon={<MdSend />}
                                    colorScheme="brand"
                                    borderRadius="full"
                                    size="sm"
                                    onClick={handleSendMessage}
                                    isDisabled={!chatInput.trim() || isStreaming}
                                    isLoading={isStreaming}
                                />
                            </HStack>
                        </Box>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <RenameDialog
                isOpen={isRenameOpen}
                onClose={onRenameClose}
                initialTitle={sessionToRename?.title || ''}
                onRename={handleRenameConfirm}
            />

            {confirmConfig && (
                <ConfirmDialog
                    isOpen={isConfirmOpen}
                    onClose={onConfirmClose}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    confirmText={confirmConfig.confirmText}
                    confirmColor={confirmConfig.confirmColor}
                    onConfirm={confirmConfig.action}
                    isLoading={deleteSessionMutation.isPending}
                />
            )}
        </>
    );
}
